import React, { useState } from 'react';
import { AdminSettings, TourDefinition, TourStepConfig } from '../../types';
import { DEFAULT_INBOX_EMAIL_TOUR_STEPS } from '../../hooks/useInboxHubTour';

// ─── Constants ────────────────────────────────────────────────────────────────

/**
 * Well-known tour IDs.  When a tour with one of these IDs is created from a
 * template the editor pre-populates it with the built-in default steps.
 */
const BUILT_IN_TEMPLATES: { id: string; name: string; description: string; steps: TourStepConfig[] }[] = [
  {
    id: 'inbox-hub-email',
    name: 'Inbox Hub — Email Tour',
    description: 'Step-by-step guide to the Inbox Hub Email channel: stat tiles, filters, queue, SLA, badges, and replying.',
    steps: DEFAULT_INBOX_EMAIL_TOUR_STEPS,
  },
];

/** All data-tour anchors currently present in the frontend code */
const ANCHOR_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'None — centered modal (no spotlight)' },
  // Inbox Hub — shared
  { value: 'channel-tabs', label: 'channel-tabs — Inbox Hub: Email / WhatsApp switcher' },
  // Inbox Hub — Email
  { value: 'email-header', label: 'email-header — Inbox Hub Email: Status tiles header' },
  { value: 'email-search', label: 'email-search — Inbox Hub Email: Search bar & filter toggle' },
  { value: 'email-queue', label: 'email-queue — Inbox Hub Email: Full queue panel' },
  { value: 'email-queue-card', label: 'email-queue-card — Inbox Hub Email: First queue card (click to open)' },
  { value: 'email-sla-bar', label: 'email-sla-bar — Inbox Hub Email: First card date/time & SLA dot' },
  { value: 'email-card-badges', label: 'email-card-badges — Inbox Hub Email: First card status badges row' },
  { value: 'email-card-reply', label: 'email-card-reply — Inbox Hub Email: First card reply/conversion tiles' },
  { value: 'email-card-assign', label: 'email-card-assign — Inbox Hub Email: First card Assign button' },
  // Inbox Hub — WhatsApp
  { value: 'whatsapp-stats', label: 'whatsapp-stats — Inbox Hub WhatsApp: Live stat pills' },
  { value: 'whatsapp-workspace', label: 'whatsapp-workspace — Inbox Hub WhatsApp: 3-panel grid' },
];

const PLACEMENT_OPTIONS: { value: NonNullable<TourStepConfig['placement']>; label: string }[] = [
  { value: 'center', label: 'Center (no arrow, no spotlight)' },
  { value: 'bottom', label: 'Below element' },
  { value: 'top', label: 'Above element' },
  { value: 'left', label: 'Left of element' },
  { value: 'right', label: 'Right of element' },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function slugify(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

function newTour(name: string): TourDefinition {
  const template = BUILT_IN_TEMPLATES.find(t => slugify(name).includes(t.id.replace(/-/g, '')));
  return {
    id: slugify(name) || `tour-${Date.now()}`,
    name,
    description: '',
    published: false,
    steps: template ? template.steps.map(s => ({ ...s })) : [newStep(1)],
  };
}

function newStep(order: number): TourStepConfig {
  return {
    id: `step-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    title: `New Step ${order}`,
    description: 'Describe what this section does and what the user can do here.',
    target: '',
    placement: 'center',
    enabled: true,
  };
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function TooltipPreview({ step, stepNum, total }: { step: TourStepConfig; stepNum: number; total: number }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 shadow-md" style={{ maxWidth: 380 }}>
      <div className="h-1 bg-slate-100">
        <div
          className="h-full bg-gradient-to-r from-sky-500 to-indigo-500 transition-all"
          style={{ width: `${Math.round((stepNum / Math.max(total - 1, 1)) * 100)}%` }}
        />
      </div>
      <div className="bg-white p-4">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">
            {stepNum === 0 ? 'Introduction' : stepNum === total - 1 ? 'Complete' : `Step ${stepNum} of ${total - 2}`}
          </span>
          <span className="text-[10px] text-slate-300">Skip tour</span>
        </div>
        <h4 className="mb-1.5 text-[14px] font-black leading-snug text-slate-900 line-clamp-2">
          {step.title || 'Step title…'}
        </h4>
        <p className="text-[12px] font-medium leading-relaxed text-slate-600 line-clamp-4">
          {step.description || 'Description will appear here…'}
        </p>
        <div className="mt-3 flex items-center justify-between">
          <span className="rounded-full border border-slate-200 px-3 py-1 text-[10px] font-black uppercase text-slate-400">Back</span>
          <span className="rounded-full bg-gradient-to-r from-sky-500 to-indigo-500 px-4 py-1 text-[10px] font-black uppercase text-white">
            {stepNum === total - 1 ? 'Done' : 'Next'}
          </span>
        </div>
      </div>
    </div>
  );
}

// ─── Step editor (within a tour) ──────────────────────────────────────────────

function StepEditor({
  steps,
  onChange,
}: {
  steps: TourStepConfig[];
  onChange: (steps: TourStepConfig[]) => void;
}) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const patch = (id: string, p: Partial<TourStepConfig>) =>
    onChange(steps.map(s => (s.id === id ? { ...s, ...p } : s)));
  const move = (id: string, dir: -1 | 1) => {
    const i = steps.findIndex(s => s.id === id);
    if (i < 0) return;
    const n = i + dir;
    if (n < 0 || n >= steps.length) return;
    const arr = [...steps];
    [arr[i], arr[n]] = [arr[n], arr[i]];
    onChange(arr);
  };
  const remove = (id: string) => {
    if (!window.confirm('Remove this step?')) return;
    onChange(steps.filter(s => s.id !== id));
    if (expandedId === id) setExpandedId(null);
  };
  const add = () => {
    const s = newStep(steps.length + 1);
    onChange([...steps, s]);
    setExpandedId(s.id);
  };

  return (
    <div className="space-y-2.5">
      {steps.map((step, idx) => {
        const isOpen = expandedId === step.id;
        return (
          <div
            key={step.id}
            className={`overflow-hidden rounded-xl border transition-all ${
              step.enabled === false
                ? 'border-slate-100 bg-slate-50 opacity-55'
                : isOpen
                ? 'border-blue-200 bg-white shadow-md'
                : 'border-slate-200 bg-white shadow-sm'
            }`}
          >
            {/* Row */}
            <div
              className="flex cursor-pointer items-center gap-2.5 px-4 py-2.5"
              onClick={() => setExpandedId(isOpen ? null : step.id)}
            >
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-100 text-[10px] font-black text-slate-500">
                {idx + 1}
              </span>

              {/* Enable toggle */}
              <button
                type="button"
                onClick={e => { e.stopPropagation(); patch(step.id, { enabled: !step.enabled }); }}
                className={`relative h-4 w-7 shrink-0 rounded-full transition-colors ${step.enabled !== false ? 'bg-emerald-500' : 'bg-slate-300'}`}
                title={step.enabled !== false ? 'Enabled — click to disable' : 'Disabled — click to enable'}
              >
                <span className={`absolute top-0.5 h-3 w-3 rounded-full bg-white shadow transition-transform ${step.enabled !== false ? 'translate-x-3' : 'translate-x-0.5'}`} />
              </button>

              <div className="min-w-0 flex-1">
                <span className="truncate text-sm font-semibold text-slate-800">{step.title}</span>
              </div>

              {step.target && (
                <span className="hidden shrink-0 rounded-full border border-sky-100 bg-sky-50 px-2 py-0.5 font-mono text-[9px] font-bold text-sky-600 sm:inline">
                  {step.target}
                </span>
              )}

              <div className="flex shrink-0 gap-1" onClick={e => e.stopPropagation()}>
                <button type="button" onClick={() => move(step.id, -1)} disabled={idx === 0}
                  className="flex h-5 w-5 items-center justify-center rounded text-slate-400 hover:bg-slate-100 disabled:opacity-30">↑</button>
                <button type="button" onClick={() => move(step.id, 1)} disabled={idx === steps.length - 1}
                  className="flex h-5 w-5 items-center justify-center rounded text-slate-400 hover:bg-slate-100 disabled:opacity-30">↓</button>
              </div>

              <span className={`shrink-0 text-[11px] text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}>▾</span>
            </div>

            {/* Expanded editor */}
            {isOpen && (
              <div className="border-t border-slate-100 px-4 py-4 space-y-3">
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="flex flex-col gap-1">
                    <label className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">Title</label>
                    <input type="text" value={step.title} onChange={e => patch(step.id, { title: e.target.value })}
                      placeholder="Step title"
                      className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">Step ID <span className="font-normal text-slate-300">(read-only)</span></label>
                    <input type="text" value={step.id} readOnly className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 font-mono text-xs text-slate-400" />
                  </div>
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">Description</label>
                  <textarea rows={3} value={step.description} onChange={e => patch(step.id, { description: e.target.value })}
                    placeholder="Explain what this section does and what the user can do here."
                    className="resize-y rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100" />
                </div>

                <div className="grid gap-3 md:grid-cols-3">
                  <div className="flex flex-col gap-1">
                    <label className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">Spotlight Target</label>
                    <select value={step.target ?? ''} onChange={e => patch(step.id, { target: e.target.value || undefined })}
                      className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100">
                      {ANCHOR_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">Tooltip Placement</label>
                    <select value={step.placement ?? 'center'} onChange={e => patch(step.id, { placement: e.target.value as TourStepConfig['placement'] })}
                      className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100">
                      {PLACEMENT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">Auto-switch Channel</label>
                    <select value={step.requiredChannel ?? ''} onChange={e => patch(step.id, { requiredChannel: (e.target.value as 'EMAIL' | 'WHATSAPP') || undefined })}
                      className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100">
                      <option value="">No switch required</option>
                      <option value="EMAIL">Switch to Email Hub</option>
                      <option value="WHATSAPP">Switch to WhatsApp Hub</option>
                    </select>
                  </div>
                </div>

                {/* Preview */}
                <div>
                  <p className="mb-1.5 text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">Live Preview</p>
                  <TooltipPreview step={step} stepNum={idx} total={steps.length} />
                </div>

                <div className="flex justify-end border-t border-slate-100 pt-3">
                  <button type="button" onClick={() => remove(step.id)}
                    className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-600 transition hover:bg-rose-100">
                    Remove step
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}

      <button type="button" onClick={add}
        className="w-full rounded-xl border-2 border-dashed border-slate-200 py-3 text-sm font-semibold text-slate-400 transition hover:border-blue-300 hover:text-blue-600">
        + Add step
      </button>
    </div>
  );
}

// ─── Main TourTab ─────────────────────────────────────────────────────────────

interface TourTabProps {
  settings: AdminSettings;
  setSettings: (s: AdminSettings) => void;
  saveSettings: (s: AdminSettings) => Promise<unknown>;
}

export const TourTab: React.FC<TourTabProps> = ({ settings, setSettings, saveSettings }) => {
  // If no tours have been saved yet, seed from built-in templates so the admin
  // can see and edit them immediately. isDirty starts true so they know to save.
  const seeded = !settings.guidedTours || settings.guidedTours.length === 0;
  const initialTours: TourDefinition[] = seeded
    ? BUILT_IN_TEMPLATES.map(t => ({
        id: t.id,
        name: t.name,
        description: t.description,
        published: true,
        steps: t.steps.map(s => ({ ...s })),
      }))
    : settings.guidedTours!;

  const [tours, setTours] = useState<TourDefinition[]>(initialTours);
  const [selectedId, setSelectedId] = useState<string | null>(initialTours[0]?.id ?? null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [isDirty, setIsDirty] = useState(seeded); // start dirty if we auto-seeded from defaults
  const [showNewModal, setShowNewModal] = useState(false);
  const [newName, setNewName] = useState('');

  const selectedTour = tours.find(t => t.id === selectedId) ?? null;

  const markDirty = (updated: TourDefinition[]) => {
    setTours(updated);
    setIsDirty(true);
    setSaveMsg('');
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveMsg('');
    try {
      const updated: AdminSettings = { ...settings, guidedTours: tours };
      await saveSettings(updated);
      setSettings(updated);
      setIsDirty(false);
      setSaveMsg('Saved successfully.');
    } catch {
      setSaveMsg('Save failed — please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const createTour = () => {
    if (!newName.trim()) return;
    // Check for matching built-in template to pre-populate steps
    const templateKey = slugify(newName);
    const template = BUILT_IN_TEMPLATES.find(t => t.id === templateKey || templateKey.includes(t.id.replace(/-/g, '')));
    const tour: TourDefinition = {
      id: template ? template.id : (slugify(newName) || `tour-${Date.now()}`),
      name: newName.trim(),
      description: template?.description ?? '',
      published: false,
      steps: template ? template.steps.map(s => ({ ...s })) : [newStep(1)],
    };
    if (tours.find(t => t.id === tour.id)) {
      alert(`A tour with ID "${tour.id}" already exists. Please choose a different name.`);
      return;
    }
    const updated = [...tours, tour];
    markDirty(updated);
    setSelectedId(tour.id);
    setShowNewModal(false);
    setNewName('');
  };

  const deleteTour = (id: string) => {
    if (!window.confirm('Delete this tour? This cannot be undone.')) return;
    const updated = tours.filter(t => t.id !== id);
    markDirty(updated);
    setSelectedId(updated[0]?.id ?? null);
  };

  const patchTour = (id: string, patch: Partial<TourDefinition>) => {
    markDirty(tours.map(t => (t.id === id ? { ...t, ...patch } : t)));
  };

  const resetToDefault = (id: string) => {
    const template = BUILT_IN_TEMPLATES.find(t => t.id === id);
    if (!template) { alert('No built-in template found for this tour ID.'); return; }
    if (!window.confirm('Replace all steps with the built-in defaults for this tour? This cannot be undone until you save.')) return;
    patchTour(id, { steps: template.steps.map(s => ({ ...s })) });
  };

  return (
    <div className="flex gap-6 min-h-[600px]">
      {/* ── Left: tour list ─────────────────────────────────────────────── */}
      <div className="w-64 shrink-0 flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-black uppercase tracking-[0.2em] text-slate-500">Tours</h3>
          <button
            type="button"
            onClick={() => setShowNewModal(true)}
            className="rounded-lg bg-blue-600 px-3 py-1 text-[11px] font-bold text-white shadow-sm transition hover:bg-blue-700"
          >
            + New
          </button>
        </div>

        {tours.length === 0 && (
          <div className="flex flex-1 items-center justify-center rounded-xl border-2 border-dashed border-slate-200 p-6 text-center text-xs text-slate-400">
            No tours yet. Click <strong>+ New</strong> to create your first guided tour.
          </div>
        )}

        <div className="space-y-1.5">
          {tours.map(tour => (
            <button
              key={tour.id}
              type="button"
              onClick={() => setSelectedId(tour.id)}
              className={`w-full rounded-xl border px-4 py-3 text-left transition ${
                selectedId === tour.id
                  ? 'border-blue-300 bg-blue-50 shadow-sm'
                  : 'border-slate-200 bg-white hover:border-slate-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className={`h-2 w-2 shrink-0 rounded-full ${tour.published ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                <span className="truncate text-xs font-bold text-slate-900">{tour.name}</span>
              </div>
              <div className="mt-0.5 flex items-center gap-1.5 pl-4">
                <span className="font-mono text-[9px] text-slate-400">{tour.id}</span>
                <span className="text-[9px] text-slate-300">·</span>
                <span className="text-[9px] text-slate-400">{tour.steps.filter(s => s.enabled !== false).length} steps</span>
              </div>
            </button>
          ))}
        </div>

        {/* Save */}
        <div className="mt-auto space-y-2 pt-2">
          {saveMsg && (
            <p className={`text-center text-xs font-medium ${saveMsg.includes('fail') ? 'text-rose-600' : 'text-emerald-600'}`}>
              {saveMsg}
            </p>
          )}
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving || !isDirty}
            className="w-full rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 py-2 text-xs font-bold text-white shadow-md shadow-blue-500/25 transition hover:opacity-90 disabled:opacity-40"
          >
            {isSaving ? 'Saving…' : isDirty ? 'Save all changes' : 'All saved'}
          </button>
        </div>
      </div>

      {/* ── Right: tour editor ───────────────────────────────────────────── */}
      <div className="flex-1 min-w-0">
        {!selectedTour ? (
          <div className="flex h-full items-center justify-center rounded-2xl border-2 border-dashed border-slate-200 p-10 text-sm text-slate-400">
            Select a tour from the left to start editing, or create a new one.
          </div>
        ) : (
          <div className="space-y-5">
            {/* Tour metadata */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm space-y-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 space-y-3">
                  <div className="flex flex-col gap-1">
                    <label className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">Tour Name</label>
                    <input
                      type="text"
                      value={selectedTour.name}
                      onChange={e => patchTour(selectedTour.id, { name: e.target.value })}
                      className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-900 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">Admin Notes (not shown to users)</label>
                    <input
                      type="text"
                      value={selectedTour.description ?? ''}
                      onChange={e => patchTour(selectedTour.id, { description: e.target.value })}
                      placeholder="e.g. Covers the Email Hub workflow for new reservations staff"
                      className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    />
                  </div>
                </div>

                <div className="shrink-0 flex flex-col items-end gap-2">
                  {/* Published toggle */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-slate-500">
                      {selectedTour.published ? 'Published' : 'Draft'}
                    </span>
                    <button
                      type="button"
                      onClick={() => patchTour(selectedTour.id, { published: !selectedTour.published })}
                      className={`relative h-6 w-11 rounded-full transition-colors ${selectedTour.published ? 'bg-emerald-500' : 'bg-slate-300'}`}
                    >
                      <span className={`absolute top-1 h-4 w-4 rounded-full bg-white shadow transition-transform ${selectedTour.published ? 'translate-x-5' : 'translate-x-1'}`} />
                    </button>
                  </div>
                  <span className="max-w-[180px] text-right text-[10px] text-slate-400">
                    {selectedTour.published
                      ? 'Live — users who open this page will see the tour.'
                      : 'Draft — hidden from users until published.'}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-3 border-t border-slate-100 pt-3">
                <div className="flex flex-col gap-0.5">
                  <span className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">Tour ID</span>
                  <span className="font-mono text-xs text-slate-600">{selectedTour.id}</span>
                </div>
                <div className="ml-auto flex gap-2">
                  {BUILT_IN_TEMPLATES.some(t => t.id === selectedTour.id) && (
                    <button type="button" onClick={() => resetToDefault(selectedTour.id)}
                      className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-50">
                      Reset to defaults
                    </button>
                  )}
                  <button type="button" onClick={() => deleteTour(selectedTour.id)}
                    className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-600 transition hover:bg-rose-100">
                    Delete tour
                  </button>
                </div>
              </div>
            </div>

            {/* How to add anchors info */}
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
              <span className="font-bold">Adding new spotlight targets:</span> Add a{' '}
              <code className="rounded bg-amber-100 px-1">data-tour="my-anchor"</code> attribute to any
              element in the frontend, then select it in the Target dropdown below. Currently available
              anchors are listed in the dropdown.
            </div>

            {/* Step editor */}
            <StepEditor
              steps={selectedTour.steps}
              onChange={steps => patchTour(selectedTour.id, { steps })}
            />
          </div>
        )}
      </div>

      {/* ── New tour modal ───────────────────────────────────────────────── */}
      {showNewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <h3 className="mb-1 text-base font-black text-slate-900">Create New Tour</h3>
            <p className="mb-4 text-sm text-slate-500">
              Give the tour a name. If you use a name matching a built-in template (e.g. "Inbox Hub Email") the steps
              will be pre-populated for you.
            </p>

            <div className="mb-2 flex flex-col gap-1">
              <label className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">Tour Name</label>
              <input
                type="text"
                value={newName}
                onChange={e => setNewName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && createTour()}
                autoFocus
                placeholder="e.g. Inbox Hub Email, Charter Workflow…"
                className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-900 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
              />
              {newName.trim() && (
                <span className="text-[10px] text-slate-400">
                  Tour ID will be: <strong className="font-mono">{slugify(newName) || '…'}</strong>
                  {BUILT_IN_TEMPLATES.some(t => t.id === slugify(newName)) && (
                    <span className="ml-1 text-emerald-600 font-semibold">— built-in template available!</span>
                  )}
                </span>
              )}
            </div>

            {/* Template list hint */}
            <div className="mb-4 rounded-lg border border-slate-100 bg-slate-50 p-3 text-xs text-slate-500">
              <p className="font-semibold mb-1">Built-in templates:</p>
              {BUILT_IN_TEMPLATES.map(t => (
                <div key={t.id} className="flex items-center gap-2">
                  <code className="font-mono text-blue-600">{t.id}</code>
                  <span className="text-slate-400">→ {t.name}</span>
                </div>
              ))}
            </div>

            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => { setShowNewModal(false); setNewName(''); }}
                className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">
                Cancel
              </button>
              <button type="button" onClick={createTour} disabled={!newName.trim()}
                className="rounded-xl bg-blue-600 px-5 py-2 text-sm font-bold text-white shadow-sm transition hover:bg-blue-700 disabled:opacity-40">
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sticky save bar */}
      {isDirty && (
        <div className="fixed bottom-4 left-1/2 z-40 -translate-x-1/2 flex items-center gap-4 rounded-2xl border border-blue-200 bg-blue-50 px-5 py-3 shadow-xl">
          <p className="text-sm font-medium text-blue-800">You have unsaved changes.</p>
          <button type="button" onClick={handleSave} disabled={isSaving}
            className="rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-5 py-2 text-sm font-bold text-white shadow-md transition hover:opacity-90 disabled:opacity-50">
            {isSaving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      )}
    </div>
  );
};
